import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon, Filter } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Seo from "../components/Seo";
import Footer from "../components/Footer";
import PhotoCard from "../components/PhotoCard";
import PhotoLightbox from "../components/PhotoLightbox";
import BundleBanner from "../components/BundleBanner";
import { useCart } from "../lib/cart";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const eventId = params.get("event_id") || "";
  const bib = params.get("bib") || "";
  const photographerId = params.get("photographer_id") || "";
  const heat = params.get("heat") || "";
  const className = params.get("class_name") || "";

  const [bibInput, setBibInput] = useState(bib);
  const [eventInput, setEventInput] = useState(eventId);
  const [photographerInput, setPhotographerInput] = useState(photographerId);
  const [heatInput, setHeatInput] = useState(heat);
  const [classInput, setClassInput] = useState(className);

  const { setSearchContext } = useCart();

  const selectedEvent = events.find((e) => e.id === eventInput);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/photographers").then((r) => setPhotographers(r.data)).catch(() => {});
  }, []);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (eventId) q.event_id = eventId;
      if (bib) q.bib = bib;
      if (photographerId) q.photographer_id = photographerId;
      if (heat) q.heat = heat;
      if (className) q.class_name = className;
      const r = await api.get("/photos/search", { params: q });
      setPhotos(r.data);
      // Save search context for lead capture
      setSearchContext({ bib: bib || null, event_id: eventId || null });
    } catch (err) {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, bib, photographerId, heat, className, setSearchContext]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (eventInput) next.event_id = eventInput;
    if (bibInput) next.bib = bibInput;
    if (photographerInput) next.photographer_id = photographerInput;
    if (heatInput) next.heat = heatInput;
    if (classInput) next.class_name = classInput;
    setParams(next);
  };

  const reset = () => {
    setBibInput("");
    setEventInput("");
    setPhotographerInput("");
    setHeatInput("");
    setClassInput("");
    setParams({});
  };

  return (
    <div className="App">
      <Seo
        title="Sök enduro-foton på startnummer — Foton Enduroskola"
        description="Hitta dina enduro-bilder från Novemberkåsan, Gotland, SM och andra event. Sök på ditt startnummer och köp direkt — fotografen får 90 %."
        path="/search"
      />
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="label-overline mb-2">Sök foton</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-8">
          {bib ? <>Startnummer <span className="text-brand">#{bib}</span></> : "Hitta dina foton"}
        </h1>

        {/* Filter bar */}
        <form
          onSubmit={submit}
          className="card-surface p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-8"
          data-testid="search-form"
        >
          <input
            value={bibInput}
            onChange={(e) => setBibInput(e.target.value)}
            placeholder="STARTNUMMER"
            className="input-field font-display tracking-wider lg:col-span-1"
            data-testid="search-bib-input"
          />
          <select
            value={eventInput}
            onChange={(e) => setEventInput(e.target.value)}
            className="input-field"
            data-testid="search-event-select"
          >
            <option value="">Alla event</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
          <select
            value={photographerInput}
            onChange={(e) => setPhotographerInput(e.target.value)}
            className="input-field"
            data-testid="search-photographer-select"
          >
            <option value="">Alla fotografer</option>
            {photographers.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            value={heatInput}
            onChange={(e) => setHeatInput(e.target.value)}
            className="input-field"
            data-testid="search-heat-select"
            disabled={!selectedEvent}
          >
            <option value="">Heat / start</option>
            {(selectedEvent?.heats || []).map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          <select
            value={classInput}
            onChange={(e) => setClassInput(e.target.value)}
            className="input-field"
            data-testid="search-class-select"
            disabled={!selectedEvent}
          >
            <option value="">Klass</option>
            {(selectedEvent?.classes || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-1" data-testid="search-submit">
              <SearchIcon className="w-4 h-4" />Sök
            </button>
            <button type="button" onClick={reset} className="btn-secondary px-4" data-testid="search-reset">
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </form>

        <BundleBanner className="mb-4" />

        {/* Results */}
        {loading ? (
          <div className="text-center py-20 text-white/50" data-testid="search-loading">Laddar foton...</div>
        ) : photos.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="search-empty">
            <SearchIcon className="w-12 h-12 mx-auto text-white/20 mb-4" />
            <p className="font-display uppercase text-2xl text-white mb-2">Inga foton hittades</p>
            <p className="text-white/60 mb-4">
              {bib ? `Inget för startnummer #${bib} ännu.` : "Justera filtren eller välj ett event."}
            </p>
            <Link to="/" className="btn-secondary inline-flex">Tillbaka till start</Link>
          </div>
        ) : (
          <>
            <p className="text-white/60 text-sm mb-4" data-testid="search-results-count">
              <span className="text-brand font-bold">{photos.length}</span> foton hittade
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
              {photos.map((p, i) => (
                <PhotoCard key={p.id} photo={p} onClick={() => setLightboxIndex(i)} />
              ))}
            </div>
          </>
        )}
      </div>
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
      <Footer />
    </div>
  );
}
